import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ShieldCheck, ArrowLeft, ShoppingCart, Loader2, Wrench, Truck, Copy, Check, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import ProductImage from "@/components/ProductImage";
import ProductCard from "@/components/ProductCard";
import PlaceholderNote from "@/components/PlaceholderNote";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getBrand, getCategory, formatPrice, specChips, displayPrice, resolveImage, categoryAccent, SPEC_LABELS } from "@/data/catalog";
import { useCart } from "@/context/CartContext";
import { fetchProduct, fetchProducts } from "@/lib/api";
import { SITE } from "@/data/site";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [copied, setCopied] = useState(false);
const [recentlyViewed, setRecentlyViewed] = useState([]);
  useEffect(() => {
    setLoading(true);
    setQty(1);
    setActiveImg(0);
    fetchProduct(slug)
      .then(setProduct)
      .catch(() => setProduct(false))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    fetchProducts()
      .then((list) => {
        const cats = product.categories || [product.categorySlug];
        setRelated(list.filter((p) => p.slug !== product.slug && (p.categories || [p.categorySlug]).some((c) => cats.includes(c))).slice(0, 4));
      })
      .catch(() => {});
  }, [product]);
useEffect(() => {
  if (!product?.slug) return;

  try {
    const key = "cahvac_recently_viewed";
    const saved = JSON.parse(localStorage.getItem(key) || "[]");
    const previous = Array.isArray(saved) ? saved : [];

    const updated = [
      product.slug,
      ...previous.filter((slug) => slug !== product.slug)
    ].slice(0, 9);

    localStorage.setItem(key, JSON.stringify(updated));

    const otherSlugs = updated.filter((slug) => slug !== product.slug);

    if (otherSlugs.length === 0) {
      setRecentlyViewed([]);
      return;
    }

    fetchProducts()
      .then((products) => {
        const bySlug = new Map(products.map((p) => [p.slug, p]));
        setRecentlyViewed(
          otherSlugs.map((slug) => bySlug.get(slug)).filter(Boolean).slice(0, 8)
        );
      })
      .catch(() => setRecentlyViewed([]));
  } catch {
    setRecentlyViewed([]);
  }
}, [product]);
  if (loading) {
    return <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 lg:px-8">
        <Seo title="Product not found | CA HVAC Direct" description="This product could not be found." />
        <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Product not found</h1>
        <Button asChild className="mt-6 rounded-sm bg-sky-700 hover:bg-sky-800"><Link to="/shop">Back to Shop</Link></Button>
      </div>
    );
  }

  const brand = getBrand(product.brandSlug);
  const category = getCategory(product.categorySlug);
  const chips = specChips(product);
  const dp = displayPrice(product);
  const warm = categoryAccent(product.categorySlug) === "warm";
  const specEntries = Object.entries(product.specs || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  const images = (product.images || []).map(resolveImage);
  const extraCats = (product.categories || []).filter((c) => c !== product.categorySlug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    category: category?.name,
    brand: { "@type": "Brand", name: brand?.name },
    mpn: product.model,
    ...(product.sku ? { sku: product.sku } : {}),
  };

  const copyModel = () => {
    navigator.clipboard?.writeText(product.model).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const add = () => {
    addItem(product, qty);
    toast.success(dp.mode === "show" ? "Added to cart" : "Added to your quote request", { description: `${product.name} × ${qty}` });
  };
  const primary = () => {
    add();
    navigate(dp.mode === "show" ? "/checkout" : "/cart");
  };

  return (
    <div className="bg-slate-50">
      <Seo
        title={`${brand?.name} ${product.name} (${product.model}) | CA HVAC Direct`}
        description={(product.shortDescription || `${product.name} by ${brand?.name}. Model ${product.model}.`).slice(0, 160)}
        type="product"
        jsonLd={jsonLd}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <nav className="font-mono text-[11px] text-slate-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-sky-700">Home</Link>
          <span className="px-1.5">/</span>
          <Link to="/shop" className="hover:text-sky-700">Catalog</Link>
          <span className="px-1.5">/</span>
          <Link to={`/category/${category?.slug}`} className="hover:text-sky-700">{category?.name}</Link>
        </nav>

        <div className="mt-6 grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
          {/* Gallery */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-white">
              {images.length > 0 ? (
                <img src={images[activeImg]} alt={product.name} className="h-full w-full object-contain" />
              ) : (
                <ProductImage product={product} />
              )}
              <div className="absolute left-4 top-4 flex flex-col gap-1.5">
                {product.clearance && <span className="rounded-sm bg-gradient-to-r from-orange-600 to-amber-600 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">Clearance</span>}
                {product.featured && !product.clearance && <span className="rounded-sm border border-sky-700/60 bg-sky-950 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-widest text-sky-300">Featured</span>}
              </div>
            </div>
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-3">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden rounded-sm border ${i === activeImg ? "border-sky-600" : "border-slate-200"}`}>
                    <img src={img} alt="" className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Purchase command center */}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-sky-700">{brand?.name}</span>
              {product.specs?.refrigerant && <span className="rounded-sm border border-cyan-200 bg-cyan-50 px-2 py-0.5 font-mono text-[10px] font-bold text-cyan-800">{product.specs.refrigerant}</span>}
            </div>
            <h1 className="mt-2 font-heading text-3xl font-black leading-tight tracking-tight text-slate-900 sm:text-4xl">{product.name}</h1>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button onClick={copyModel} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-2.5 py-1 font-mono text-xs text-slate-700 transition-colors duration-150 hover:border-sky-600 hover:text-sky-700" data-testid="copy-model">
                Model: {product.model} {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
              {product.sku && <span className="font-mono text-xs text-slate-500">SKU: {product.sku}</span>}
              <Link to={`/category/${category?.slug}`}><Badge variant="secondary" className="rounded-sm">{category?.name}</Badge></Link>
              {extraCats.map((c) => <Link key={c} to={`/category/${c}`}><Badge variant="secondary" className="rounded-sm">{getCategory(c)?.name}</Badge></Link>)}
            </div>

            {chips.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {chips.map((c, i) => <span key={i} className="rounded-sm bg-white px-2.5 py-1 font-mono text-[11px] font-medium text-slate-700 ring-1 ring-slate-200">{c}</span>)}
              </div>
            )}

            {/* Price block */}
            <div className={`mt-6 rounded-lg border p-5 ${dp.mode === "show" ? "border-slate-200 bg-white" : "border-slate-200 bg-white"}`}>
              {dp.mode === "show" ? (
                <div className="flex items-baseline gap-3">
                  <span className="font-heading text-3xl font-black tracking-tight text-slate-900">{formatPrice(dp.price)}</span>
                  {dp.original && dp.original > dp.price && <span className="text-lg text-slate-400 line-through">{formatPrice(dp.original)}</span>}
                </div>
              ) : (
                <div>
                  <span className="font-heading text-2xl font-black tracking-tight text-slate-900">Request Price</span>
                  <p className="mt-1 text-sm text-slate-600">Add this unit to a quote request and we'll get you current pricing, availability and lead time — no account required.</p>
                </div>
              )}
              <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-slate-400">{product.status || (dp.mode === "show" ? "Available to order" : "Fast quote · homeowners & trade welcome")}</p>

              <div className="mt-4 flex items-center gap-4">
                <span className="text-sm font-medium text-slate-700">Qty</span>
                <div className="inline-flex items-center rounded-sm border border-slate-300 bg-white">
                  <button className="flex h-9 w-9 items-center justify-center text-slate-600 hover:bg-slate-50" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity" data-testid="qty-decrease"><Minus className="h-4 w-4" /></button>
                  <span className="w-10 text-center font-mono text-sm font-semibold" data-testid="qty-value">{qty}</span>
                  <button className="flex h-9 w-9 items-center justify-center text-slate-600 hover:bg-slate-50" onClick={() => setQty((q) => q + 1)} aria-label="Increase quantity" data-testid="qty-increase"><Plus className="h-4 w-4" /></button>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
                <Button className={`flex-1 rounded-sm px-6 font-semibold text-white ${warm ? "bg-orange-600 hover:bg-orange-700" : "bg-sky-700 hover:bg-sky-800"}`} onClick={add} data-testid="detail-add-to-cart">
                  <ShoppingCart className="h-4 w-4" /> {dp.mode === "show" ? "Add to Cart" : "Add to Quote Request"}
                </Button>
                <Button variant="outline" className="flex-1 rounded-sm border-slate-300 px-6 font-semibold" onClick={primary} data-testid="detail-buy-now">
                  {dp.mode === "show" ? "Buy Now" : "Request a Quote"}
                </Button>
              </div>
              <Button asChild variant="ghost" className="mt-1 w-full rounded-sm text-sky-700 hover:text-sky-800" data-testid="detail-contact">
                <a href={SITE.phoneHref}><Phone className="h-4 w-4" /> Call {SITE.phone}</a>
              </Button>
            </div>

            {/* Fulfillment & service trio */}
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-sm border border-slate-200 bg-white p-4">
                <Truck className="h-5 w-5 text-sky-700" />
                <p className="mt-2 text-xs font-semibold text-slate-900">Curbside / Local Delivery</p>
                <p className="mt-0.5 text-[11px] text-slate-500">Distance-based; confirmed after order.</p>
              </div>
              <div className="rounded-sm border border-slate-200 bg-white p-4">
                <ShieldCheck className="h-5 w-5 text-sky-700" />
                <p className="mt-2 text-xs font-semibold text-slate-900">Manufacturer Warranty</p>
                <p className="mt-0.5 text-[11px] text-slate-500">Coverage varies by manufacturer.</p>
              </div>
              
            </div>
          </div>
        </div>

        {/* Engineering tabs */}
        <div className="mt-12 rounded-xl border border-slate-200 bg-white p-6 sm:p-8">
          <Tabs defaultValue="specs">
            <TabsList className="flex-wrap rounded-sm">
              <TabsTrigger value="specs" data-testid="tab-specs">Specifications</TabsTrigger>
              <TabsTrigger value="description" data-testid="tab-description">Overview</TabsTrigger>
              <TabsTrigger value="warranty" data-testid="tab-warranty">Warranty</TabsTrigger>
            </TabsList>

            <TabsContent value="specs" className="mt-6">
              {specEntries.length > 0 ? (
                <dl className="grid max-w-3xl grid-cols-1 overflow-hidden rounded-lg border border-slate-200 sm:grid-cols-2">
                  {specEntries.map(([k, v], idx) => (
                    <div key={k} className={`flex justify-between gap-4 px-4 py-3 text-sm ${idx % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
                      <dt className="text-slate-500">{SPEC_LABELS[k] || k}</dt>
                      <dd className="text-right font-mono font-medium text-slate-900">{k === "btu" ? `${Number(v).toLocaleString()} BTU` : String(v)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <PlaceholderNote>Full technical specifications for this model will be added shortly.</PlaceholderNote>
              )}
              <p className="mt-4 max-w-3xl text-xs text-slate-400">Additional engineering data (dimensions, weight, electrical and sound ratings) available on request.</p>
            </TabsContent>

            <TabsContent value="description" className="mt-6">
              {product.description ? (
                <div className="max-w-3xl whitespace-pre-line leading-relaxed text-slate-700">{product.description}</div>
              ) : (
                <PlaceholderNote>A detailed description for this model will be added shortly.</PlaceholderNote>
              )}
              {product.features && product.features.length > 0 && (
                <ul className="mt-6 max-w-3xl list-disc space-y-1 pl-5 text-sm text-slate-700">
                  {product.features.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="warranty" className="mt-6">
              <div className="max-w-3xl space-y-4">
                <p className="leading-relaxed text-slate-700">
                  This equipment is covered by the applicable <strong>manufacturer warranty</strong>. Warranty length,
                  registration requirements, eligibility and exclusions depend on the manufacturer and specific equipment.
                  See our <Link to="/policies/warranty" className="font-semibold text-sky-700 hover:underline">Warranty information</Link>.
                </p>
              </div>
            </TabsContent>

            
          </Tabs>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-xl font-black tracking-tight text-slate-900 sm:text-2xl">Related Equipment</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      {recentlyViewed.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-black tracking-tight text-slate-900 sm:text-2xl">
            Recently Viewed
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {recentlyViewed.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}
        <div className="mt-10">
          <Button asChild variant="ghost" className="rounded-sm text-slate-600"><Link to="/shop"><ArrowLeft className="h-4 w-4" /> Back to Catalog</Link></Button>
        </div>
      </div>
    </div>
  );
}
